import React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import FeatureFlags from '@components/featureFlags';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { extractRgbFromColor } from '@helpers';
import NcFileContent from '@components/common/NCFile';
import { scale as initialScale } from '@components/frame';
import { BOTTOM_SHEET_TOP_OFFSET_WEB, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import NCFileDiffSheet from '@components/ncFileVersions/components/NCFileDiffSheet';
import { WithColor } from '@styles/colors';
import { useManageNCFileContentWithVersion } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationNcFilesScreen/NCFileContentWithVersion/hooks/useManageNCFileContentWithVersion';

export interface NCFileContentWithVersionProps extends WithColor {
  scale: ScaleFunction;
  style?: StyleProp<ViewStyle>;
  font?: typeof initialFont;
  bg?: string;
  ncFileId: number;
}

const NCFileContentWithVersion = ({ ncFileId, C, style, font = initialFont, bg = 'Background/Light/Field', scale = initialScale }: NCFileContentWithVersionProps) => {
  const { ncFileData, versions, versionNumber, versionsToCompare, getNCFile, openVersionPicker, closeComparison, replaceVersionForComparison } =
    useManageNCFileContentWithVersion({ ncFileId, C });

  if (!ncFileData) {
    return null;
  }

  const baseColorForGradient = extractRgbFromColor(C(bg));

  return (
    <>
      <View
        style={[
          getBoxShadowStyle({ type: 'Field', C }),
          {
            backgroundColor: C(bg),
            flex: 1,
            borderRadius: scale(18),
          },
          style,
        ]}
      >
        <View
          style={{
            width: '100%',
            borderRadius: scale(18),
            overflow: 'hidden',
          }}
        >
          {Boolean(versionNumber) && (
            <FeatureFlags {...{ name: 'ncfile-versions' }}>
              <InputLine
                {...{
                  testID: TEST_IDS.operatorPanel.information.ncFiles.versionRow,
                  bg,
                  font,
                  scale,
                  data: [],
                  index: 1,
                  i: {
                    action: openVersionPicker,
                    name: 'Version',
                    value: versionNumber,
                    isText: true,
                  },
                  C,
                }}
              />
            </FeatureFlags>
          )}
        </View>
        <View style={{ flex: 1 }}>
          <NcFileContent
            {...{
              C,
              font,
              scale,
              ncFileName: ncFileData.ncFileName,
              ncFileId: ncFileData.ncFileId,
              hideShowFullNCFileButton: true,
              backgroundColor: bg,
              singleColor: true,
              ncFileTextColor: C('Label/Dark/Primary'),
              baseColor: baseColorForGradient,
            }}
          />
        </View>
      </View>

      <NCFileDiffSheet
        C={C}
        visible={Boolean(versionsToCompare)}
        oldVersionData={versionsToCompare?.[0]}
        newVersionData={versionsToCompare?.[1]}
        getNCFile={getNCFile}
        availableVersions={versions}
        onReplaceVersion={replaceVersionForComparison}
        onClose={closeComparison}
        ncFileName={ncFileData.ncFileName}
        webTopOffset={BOTTOM_SHEET_TOP_OFFSET_WEB}
      />
    </>
  );
};

export default NCFileContentWithVersion;
